import { memo, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import ConfigurationStatusChip from './ConfigurationStatusChip';
import SubscribeOfferingPanel from './SubscribeOfferingPanel';
import SubscribedOfferingDetailPanel from './SubscribedOfferingDetailPanel';
import { useSubscribedOfferings } from '../hooks/useSubscribedOfferings';

interface SubscribedOfferingsTabContentProps {
  customerId: string | undefined;
  isActive: boolean;
}

const SubscribedOfferingsTabContent = memo<SubscribedOfferingsTabContentProps>(({ customerId, isActive }) => {
  const {
    subscriptions,
    offeringById,
    subscribableOfferings,
    loading,
    error,
    busy,
    subscribe,
    revoke,
    toggleCapability,
    saveOperatorInputs,
    snackbar,
    closeSnackbar,
  } = useSubscribedOfferings({ customerId, isActive });

  const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null);
  const [view, setView] = useState<'detail' | 'subscribe'>('detail');

  useEffect(() => {
    if (!isActive) {
      setSelectedOfferingId(null);
      setView('detail');
    }
  }, [isActive]);

  const selected = subscriptions.find((s) => s.offering_id === selectedOfferingId) ?? null;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5 }}>Subscriptions</Typography>
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => { setSelectedOfferingId(null); setView('subscribe'); }}
          >
            Subscribe to an offering
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && subscriptions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No subscribed offerings yet.
          </Typography>
        )}

        <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
          {subscriptions.map((sub, idx) => {
            const offering = offeringById.get(sub.offering_id);
            return (
              <Box key={sub.offering_id}>
                {idx > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    selected={view === 'detail' && selectedOfferingId === sub.offering_id}
                    onClick={() => { setSelectedOfferingId(sub.offering_id); setView('detail'); }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemText
                      primary={offering?.display_name ?? sub.offering_id}
                      secondary={`v${sub.pinned_offering_version}`}
                      slotProps={{ secondary: { sx: { fontFamily: 'monospace', opacity: 0.7 } } }}
                    />
                    <ConfigurationStatusChip status={sub.status} />
                  </ListItemButton>
                </ListItem>
              </Box>
            );
          })}
        </List>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {view === 'subscribe' ? (
          <SubscribeOfferingPanel
            offerings={subscribableOfferings}
            busy={busy}
            onCancel={() => setView('detail')}
            onSubscribe={subscribe}
          />
        ) : selected ? (
          <SubscribedOfferingDetailPanel
            key={selected.offering_id}
            subscription={selected}
            offering={offeringById.get(selected.offering_id)}
            busy={busy}
            onToggleCapability={toggleCapability}
            onSaveOperatorInputs={saveOperatorInputs}
            onRevoke={async (offeringId) => {
              const ok = await revoke(offeringId);
              if (ok) setSelectedOfferingId(null);
              return ok;
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              Select a subscribed offering, or subscribe to a new one.
            </Typography>
          </Box>
        )}
      </Box>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
});

SubscribedOfferingsTabContent.displayName = 'SubscribedOfferingsTabContent';
export default SubscribedOfferingsTabContent;
